import * as React2 from 'react';
import { Badge, Textarea, cn, ErrorBoundary, GraphFrame, EmptyState, Input, Button, Skeleton } from '@authe/ui-core';
import { CommentAnchorKind, WORKSPACE_ANCHOR, WorkspaceKind, CollaborationProvider, CommentThread, useSharedState, useOptionalSharedState, useCollaboration, useWorkspaceMeta, usePresence, PresenceAvatars, userColor } from '@authe/collaboration';
export { CommentAnchorKind, WORKSPACE_ANCHOR, WorkspaceKind, anchorFor } from '@authe/collaboration';
import { jsx, jsxs } from 'react/jsx-runtime';
import { createLogger } from '@authe/fe-commons/logging';
import { overlayEdit, hiddenRefs, hiddenCount, isAuthoredId, draftEdge, draftNode, applySceneOverlay } from '@authe/fe-commons/graph-overlay';
import { emptySceneOverlay, layerForSubject, SUBJECT_ANY } from '@authe/fe-commons/models';
import { focusFromTransactionRow, parseFocusNodeId, focusNodeId } from '@authe/fe-commons/focus';
export { addressNodeId, entityNodeId, focusAddress, focusEntity, focusFromTransactionRow, focusNodeId, parseFocusNodeId, sameFocusTarget, txNodeId } from '@authe/fe-commons/focus';

// src/InvestigationWorkspace.tsx
var WorkspaceViewId = {
  TABLE: "table",
  GRAPH: "graph",
  SEMANTIC: "semantic",
  PIPELINE: "pipeline",
  NOTES: "notes"
};
function viewAnchor(viewId, label) {
  return { kind: CommentAnchorKind.CUSTOM, id: `view:${viewId}`, label: label ?? viewId };
}
function panelCommentAnchor(panel) {
  return panel.commentAnchor ?? WORKSPACE_ANCHOR;
}
var DEFAULT_LABELS = {
  table: "Table",
  graph: "Graph",
  semantic: "Semantic",
  pipeline: "Pipeline",
  notes: "Notes"
};
function standardInvestigationPanels(init) {
  const labels = { ...DEFAULT_LABELS, ...init.labels };
  const panels = [];
  if (init.table !== void 0) {
    panels.push({ id: WorkspaceViewId.TABLE, label: labels.table, content: init.table });
  }
  if (init.graph !== void 0) {
    panels.push({ id: WorkspaceViewId.GRAPH, label: labels.graph, content: init.graph });
  }
  if (init.semantic !== void 0) {
    panels.push({ id: WorkspaceViewId.SEMANTIC, label: labels.semantic, content: init.semantic });
  }
  if (init.pipeline !== void 0) {
    panels.push({ id: WorkspaceViewId.PIPELINE, label: labels.pipeline, content: init.pipeline });
  }
  return panels;
}
function WorkspaceProvider({
  scope,
  user,
  title,
  chainId,
  persist,
  readOnly,
  transport,
  fallback,
  errorFallback,
  children
}) {
  const meta = React2.useMemo(
    () => ({
      title: title ?? "Investigation",
      kind: WorkspaceKind.INVESTIGATION,
      ...chainId !== void 0 ? { chainId } : {}
    }),
    [title, chainId]
  );
  return /* @__PURE__ */ jsx(
    CollaborationProvider,
    {
      scope,
      user,
      transport,
      meta,
      persist,
      readOnly,
      errorFallback,
      fallback,
      children
    }
  );
}
function WorkspaceComments({
  anchor = WORKSPACE_ANCHOR,
  users,
  title = "Discussion",
  hideResolved,
  className
}) {
  return /* @__PURE__ */ jsx(
    CommentThread,
    {
      anchor,
      users,
      title,
      hideResolved,
      className
    }
  );
}
var WORKSPACE_PACKAGE = "workspace";
createLogger(`fe:${WORKSPACE_PACKAGE}`);
var DEFAULT_NOTES_KEY = "investigation:notes";
var GRAPH_ANNOTATIONS_KEY = "investigation:graph-annotations";
function NotesPanel({
  stateKey = DEFAULT_NOTES_KEY,
  title = "Notes",
  placeholder = "Capture findings, hypotheses, and next steps. Everyone on the team sees edits live.",
  readOnly,
  className
}) {
  const [notes, setNotes] = useSharedState(stateKey, "");
  return /* @__PURE__ */ jsxs("div", { className: cn("flex h-full min-h-0 flex-col gap-2 p-3", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
      /* @__PURE__ */ jsx("h3", { className: "text-sm font-semibold", children: title }),
      /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "font-normal", children: "Shared \xB7 live" })
    ] }),
    /* @__PURE__ */ jsx(
      Textarea,
      {
        value: notes,
        onChange: (event) => setNotes(event.target.value),
        placeholder,
        readOnly,
        spellCheck: true,
        className: "min-h-0 flex-1 resize-none font-mono text-sm leading-relaxed"
      }
    ),
    /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "Markdown-friendly. Synced in real time across the team (last edit wins)." })
  ] });
}
function WorkspaceLoading() {
  return /* @__PURE__ */ jsxs("div", { className: "flex h-full min-h-0 flex-col gap-3 p-4", "aria-busy": true, children: [
    /* @__PURE__ */ jsx(Skeleton, { className: "h-8 w-56" }),
    /* @__PURE__ */ jsx(Skeleton, { className: "h-7 w-72" }),
    /* @__PURE__ */ jsx(Skeleton, { className: "flex-1" }),
    /* @__PURE__ */ jsx("span", { className: "sr-only", children: "Connecting to the workspace\u2026" })
  ] });
}
function TabPresenceDots({ peers }) {
  return /* @__PURE__ */ jsx("span", { className: "ml-0.5 flex -space-x-1", "aria-hidden": true, children: peers.slice(0, 3).map((peer) => /* @__PURE__ */ jsx(
    "span",
    {
      title: peer.user.name,
      className: "h-2 w-2 rounded-full ring-1 ring-background",
      style: { background: userColor(peer.user) }
    },
    peer.clientId
  )) });
}
function WorkspaceTab({
  view,
  active,
  peers,
  onSelect
}) {
  return /* @__PURE__ */ jsxs(
    "button",
    {
      type: "button",
      role: "tab",
      "aria-selected": active,
      onClick: onSelect,
      className: cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors",
        active ? "bg-muted font-medium text-foreground" : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
      ),
      children: [
        view.icon ? /* @__PURE__ */ jsx("span", { className: "shrink-0", children: view.icon }) : null,
        /* @__PURE__ */ jsx("span", { children: view.label }),
        peers.length > 0 ? /* @__PURE__ */ jsx(TabPresenceDots, { peers }) : null
      ]
    }
  );
}
function WorkspaceFrame(props) {
  const { readOnly } = useCollaboration();
  const {
    panels,
    notes,
    notesKey,
    comments,
    commentsOpenByDefault,
    users,
    editableTitle,
    fallbackTitle,
    defaultView,
    onViewChange,
    viewRequest,
    headerExtra,
    className
  } = props;
  const views = React2.useMemo(() => {
    if (!notes) return panels;
    return [
      ...panels,
      {
        id: WorkspaceViewId.NOTES,
        label: "Notes",
        content: /* @__PURE__ */ jsx(NotesPanel, { stateKey: notesKey, readOnly }),
        commentAnchor: viewAnchor(WorkspaceViewId.NOTES, "Notes")
      }
    ];
  }, [panels, notes, notesKey, readOnly]);
  const firstId = views[0]?.id ?? "";
  const initialId = defaultView && views.some((v) => v.id === defaultView) ? defaultView : firstId;
  const [activeView, setActiveView] = React2.useState(initialId);
  const [mounted, setMounted] = React2.useState(() => new Set(initialId ? [initialId] : []));
  const [commentsOpen, setCommentsOpen] = React2.useState(commentsOpenByDefault);
  React2.useEffect(() => {
    if (views.length === 0) return;
    if (!views.some((v) => v.id === activeView)) {
      setActiveView(views[0].id);
      setMounted((prev) => new Set(prev).add(views[0].id));
    }
  }, [views, activeView]);
  const { meta, setTitle } = useWorkspaceMeta();
  const canonicalTitle = props.onTitleChange ? props.fallbackTitle : meta.title ?? props.fallbackTitle;
  const [titleDraft, setTitleDraft] = React2.useState(canonicalTitle);
  const [editingTitle, setEditingTitle] = React2.useState(false);
  React2.useEffect(() => {
    if (!editingTitle) setTitleDraft(canonicalTitle);
    if (props.onTitleChange && !readOnly && meta.title !== props.fallbackTitle) setTitle(props.fallbackTitle);
  }, [canonicalTitle, editingTitle, props.onTitleChange, props.fallbackTitle, readOnly, meta.title, setTitle]);
  const presence = usePresence({ includeSelf: false });
  const { setFocus } = presence;
  const activePanel = views.find((v) => v.id === activeView);
  React2.useEffect(() => {
    if (!activeView) return;
    setFocus(viewAnchor(activeView, activePanel?.label));
    return () => setFocus(null);
  }, [activeView, setFocus]);
  const select = React2.useCallback((id) => {
    setActiveView(id);
    setMounted((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);
  const [honouredNonce, setHonouredNonce] = React2.useState(null);
  if (viewRequest && viewRequest.nonce !== honouredNonce) {
    setHonouredNonce(viewRequest.nonce);
    if (views.some((v) => v.id === viewRequest.id)) {
      setActiveView(viewRequest.id);
      setMounted((prev) => prev.has(viewRequest.id) ? prev : new Set(prev).add(viewRequest.id));
    }
  }
  const reportedView = React2.useRef(activeView);
  React2.useEffect(() => {
    if (reportedView.current === activeView) return;
    reportedView.current = activeView;
    onViewChange?.(activeView);
  }, [activeView, onViewChange]);
  const contentCache = React2.useRef(/* @__PURE__ */ new Map());
  const renderContent = React2.useCallback((view) => {
    if (typeof view.content !== "function") return view.content;
    const cached = contentCache.current.get(view.id);
    if (cached !== void 0) return cached;
    const node = view.content();
    contentCache.current.set(view.id, node);
    return node;
  }, []);
  if (views.length === 0) {
    return /* @__PURE__ */ jsx("div", { className: cn("flex h-full min-h-0 items-center justify-center", className), children: /* @__PURE__ */ jsx(
      EmptyState,
      {
        size: "lg",
        title: "Nothing to investigate yet",
        description: "Add table, graph, pipeline, or custom panels to this workspace."
      }
    ) });
  }
  const showCommentsToggle = comments && !activePanel?.hideComments;
  return /* @__PURE__ */ jsxs("div", { className: cn("flex h-full min-h-0 flex-col bg-background", className), children: [
    /* @__PURE__ */ jsxs("header", { className: "flex items-center gap-3 border-b px-3 py-2", children: [
      editableTitle && !readOnly ? /* @__PURE__ */ jsx(
        Input,
        {
          value: titleDraft,
          onFocus: () => setEditingTitle(true),
          onChange: (event) => setTitleDraft(event.target.value),
          onBlur: () => {
            setEditingTitle(false);
            const next = titleDraft.trim();
            if (next && next !== canonicalTitle) (props.onTitleChange ?? setTitle)(next);
            else setTitleDraft(canonicalTitle);
          },
          onKeyDown: (event) => {
            if (event.key === "Enter") event.currentTarget.blur();
          },
          placeholder: fallbackTitle,
          "aria-label": "Investigation title",
          className: "h-8 max-w-xs border-transparent px-1 text-sm font-semibold shadow-none hover:border-input focus-visible:ring-1"
        }
      ) : /* @__PURE__ */ jsx("h2", { className: "truncate text-sm font-semibold", children: canonicalTitle }),
      /* @__PURE__ */ jsxs("div", { className: "ml-auto flex items-center gap-2", children: [
        headerExtra,
        /* @__PURE__ */ jsx(PresenceAvatars, { max: 6 }),
        showCommentsToggle ? /* @__PURE__ */ jsx(
          Button,
          {
            type: "button",
            size: "sm",
            variant: commentsOpen ? "secondary" : "ghost",
            onClick: () => setCommentsOpen((open) => !open),
            children: "Comments"
          }
        ) : null
      ] })
    ] }),
    /* @__PURE__ */ jsx(
      "nav",
      {
        role: "tablist",
        "aria-label": "Workspace views",
        className: "flex items-center gap-1 overflow-x-auto border-b px-2 py-1.5",
        children: views.map((view) => /* @__PURE__ */ jsx(
          WorkspaceTab,
          {
            view,
            active: view.id === activeView,
            peers: presence.focusedOn(viewAnchor(view.id)),
            onSelect: () => select(view.id)
          },
          view.id
        ))
      }
    ),
    /* @__PURE__ */ jsxs("div", { className: "flex min-h-0 flex-1", children: [
      /* @__PURE__ */ jsx("div", { className: "relative min-w-0 flex-1", children: views.map((view) => {
        const isActive = view.id === activeView;
        const keepMounted = view.keepMounted !== false;
        const shouldRender = isActive || mounted.has(view.id) && keepMounted;
        if (!shouldRender) return null;
        return /* @__PURE__ */ jsx(
          "div",
          {
            role: "tabpanel",
            "aria-label": view.label,
            hidden: !isActive,
            className: "absolute inset-0 overflow-hidden",
            children: /* @__PURE__ */ jsx(ErrorBoundary, { boundary: `workspace:view:${view.id}`, children: renderContent(view) })
          },
          view.id
        );
      }) }),
      comments && commentsOpen ? /* @__PURE__ */ jsx("aside", { className: "w-80 shrink-0 overflow-auto border-l p-3", children: /* @__PURE__ */ jsx(
        WorkspaceComments,
        {
          anchor: activePanel ? panelCommentAnchor(activePanel) : void 0,
          users
        }
      ) }) : null
    ] })
  ] });
}
function InvestigationWorkspace(props) {
  const {
    scope,
    user,
    users,
    persist,
    transport,
    fallback,
    errorFallback,
    title,
    chainId,
    editableTitle = true,
    readOnly = false,
    onTitleChange,
    panels,
    notes = true,
    notesKey,
    comments = true,
    defaultView,
    viewRequest,
    commentsOpenByDefault = false,
    onViewChange,
    headerExtra,
    children,
    className
  } = props;
  const resolvedUsers = users ?? [user];
  return /* @__PURE__ */ jsxs(
    WorkspaceProvider,
    {
      scope,
      user,
      title,
      chainId,
      persist,
      readOnly,
      transport,
      errorFallback,
      fallback: fallback ?? /* @__PURE__ */ jsx(WorkspaceLoading, {}),
      children: [
        /* @__PURE__ */ jsx(
          WorkspaceFrame,
          {
            panels,
            notes,
            notesKey,
            comments,
            commentsOpenByDefault,
            users: resolvedUsers,
            editableTitle,
            fallbackTitle: title ?? "Investigation",
            onTitleChange,
            defaultView,
            viewRequest,
            onViewChange,
            headerExtra,
            className
          }
        ),
        children
      ]
    }
  );
}
var PERSIST_DEBOUNCE_MS = 1500;
function useGraphAnnotations(options = {}) {
  const { subjectKey, readOnly = false, persisted, onPersist } = options;
  const [stored, setStored] = useOptionalSharedState(
    annotationRoomKey(subjectKey, options.stateKey),
    EMPTY
  );
  const durable = React2.useMemo(
    () => layerForSubject(persisted, subjectKey ?? SUBJECT_ANY),
    [persisted, subjectKey]
  );
  const overlay = React2.useMemo(() => normalize(stored), [stored]);
  useDurableMirror({
    overlay,
    persisted: durable,
    onPersist,
    subjectKey: subjectKey ?? SUBJECT_ANY,
    readOnly,
    setStored
  });
  const edit = React2.useCallback(
    (fn) => {
      if (readOnly) return;
      setStored((current) => fn(normalize(current)));
    },
    [readOnly, setStored]
  );
  const actions = React2.useMemo(
    () => ({
      // Minted here rather than in the caller for the same reason the edge is:
      // the id has to be the one that lands in the overlay, and nothing else
      // identifies the node until the shared doc round-trips.
      onAddNode: (draft) => {
        if (readOnly) return null;
        const node = draftNode(draft);
        edit((o) => overlayEdit.addNode(o, node));
        return node;
      },
      // Returns the edge it drew so the caller can put the author in front of it
      // and ask what it means. Minted here rather than in the caller because the
      // id has to be the one that lands in the overlay; nothing else identifies
      // the edge until the shared doc round-trips.
      onConnect: (source, target) => {
        if (readOnly) return null;
        const edge = draftEdge({ source, target });
        edit((o) => overlayEdit.addEdge(o, edge));
        return edge;
      },
      onRelabel: (target, label) => edit((o) => overlayEdit.relabel(o, target, label)),
      onExplain: (target, reason) => edit((o) => overlayEdit.explain(o, target, reason)),
      onMove: (nodeId, position) => edit((o) => overlayEdit.moveNode(o, nodeId, position)),
      onHide: (target) => edit((o) => overlayEdit.hide(o, target)),
      // Removing is only meaningful for the layer's own elements; a derived
      // element is hidden instead, so the decode stays intact.
      onRemove: (target) => edit((o) => isAuthoredId(target.id) ? overlayEdit.remove(o, target) : o),
      onUnhide: (target) => edit((o) => overlayEdit.unhide(o, target)),
      // Defaults to the label: "restore the decoded name" is the undo an analyst
      // reaches for, and dropping a typed reason along with it would take back
      // more than they asked to.
      onRevert: (target, fields = ["label"]) => edit((o) => overlayEdit.revert(o, target, fields)),
      onRestoreHidden: () => edit(overlayEdit.restoreHidden),
      hiddenCount: hiddenCount(overlay),
      hidden: hiddenRefs(overlay),
      isPatched: (target, field) => overlayEdit.isPatched(overlay, target, field),
      reasonFor: (target) => overlayEdit.reasonOf(overlay, target)
    }),
    [edit, overlay, readOnly]
  );
  const apply = React2.useCallback(
    (scene) => applySceneOverlay(scene, [overlay]),
    [overlay]
  );
  const clear = React2.useCallback(() => edit(() => emptySceneOverlay()), [edit]);
  return {
    ...actions,
    overlay,
    apply,
    isEmpty: countElements(overlay) === 0,
    clear
  };
}
function annotationRoomKey(subjectKey, override) {
  if (override) return override;
  return subjectKey ? `${GRAPH_ANNOTATIONS_KEY}:${subjectKey}` : GRAPH_ANNOTATIONS_KEY;
}
function restoreStep(overlay, persisted) {
  if (countElements(overlay) > 0) return { action: "settle" };
  const durable = normalize(persisted ?? void 0);
  return countElements(durable) > 0 ? { action: "seed", overlay: durable } : { action: "wait" };
}
function needsMirror(overlay, persisted) {
  return JSON.stringify(overlay) !== JSON.stringify(normalize(persisted ?? void 0));
}
function useDurableMirror(input) {
  const { overlay, persisted, onPersist, subjectKey, readOnly, setStored } = input;
  const settledFor = React2.useRef(null);
  React2.useEffect(() => {
    if (readOnly || settledFor.current === subjectKey) return;
    const step = restoreStep(overlay, persisted);
    if (step.action === "wait") return;
    settledFor.current = subjectKey;
    if (step.action === "seed") setStored(() => step.overlay);
  }, [overlay, persisted, readOnly, setStored, subjectKey]);
  const persist = React2.useRef(onPersist);
  React2.useEffect(() => {
    persist.current = onPersist;
  });
  React2.useEffect(() => {
    if (readOnly || !onPersist) return void 0;
    if (!needsMirror(overlay, persisted)) return void 0;
    const handle = window.setTimeout(
      () => persist.current?.(overlay, subjectKey),
      PERSIST_DEBOUNCE_MS
    );
    return () => window.clearTimeout(handle);
  }, [overlay, persisted, subjectKey, readOnly, Boolean(onPersist)]);
}
var EMPTY = Object.freeze(emptySceneOverlay());
function normalize(body) {
  if (!body) return EMPTY;
  return {
    nodes: body.nodes ?? [],
    edges: body.edges ?? [],
    groups: body.groups ?? [],
    hidden_node_ids: body.hidden_node_ids ?? [],
    hidden_edge_ids: body.hidden_edge_ids ?? [],
    node_patches: body.node_patches ?? {},
    edge_patches: body.edge_patches ?? {},
    positions: body.positions ?? {},
    // Authorship is stamped by the API from the caller's identity when the layer
    // is published, never asserted from the client — so the live layer carries
    // the keys and leaves them empty rather than guessing at them.
    authored_by: body.authored_by ?? null,
    authored_at: body.authored_at ?? null,
    authored_reason: body.authored_reason ?? null,
    authored_scope: body.authored_scope ?? null
  };
}
function countElements(overlay) {
  return overlay.nodes.length + overlay.edges.length + overlay.groups.length + overlay.hidden_node_ids.length + overlay.hidden_edge_ids.length + Object.keys(overlay.node_patches).length + Object.keys(overlay.edge_patches).length + Object.keys(overlay.positions).length;
}

// src/graph/graphAuthoring.ts
function graphAuthoringFrom(annotations) {
  const {
    onAddNode,
    onConnect,
    onRelabel,
    onExplain,
    onMove,
    onHide,
    onRemove,
    onUnhide,
    onRevert,
    onRestoreHidden,
    hiddenCount,
    hidden,
    isPatched,
    reasonFor
  } = annotations;
  return {
    onAddNode,
    onConnect,
    onRelabel,
    onExplain,
    onMove,
    onHide,
    onRemove,
    onUnhide,
    onRevert,
    onRestoreHidden,
    hiddenCount,
    hidden,
    isPatched,
    reasonFor
  };
}
var FocusContext = React2.createContext(null);
function InvestigationFocusProvider({
  focus,
  onFocusChange,
  defaultFocus = null,
  children
}) {
  const [internal, setInternal] = React2.useState(defaultFocus);
  const isControlled = focus !== void 0;
  const current = isControlled ? focus : internal;
  const setFocus = React2.useCallback(
    (target) => {
      if (!isControlled) setInternal(target);
      onFocusChange?.(target);
    },
    [isControlled, onFocusChange]
  );
  const value = React2.useMemo(
    () => ({ focus: current, setFocus, clearFocus: () => setFocus(null) }),
    [current, setFocus]
  );
  return /* @__PURE__ */ jsx(FocusContext.Provider, { value, children });
}
function useInvestigationFocus() {
  const ctx = React2.useContext(FocusContext);
  if (!ctx) throw new Error("useInvestigationFocus must be used within an <InvestigationFocusProvider>");
  return ctx;
}
function useOptionalInvestigationFocus() {
  return React2.useContext(FocusContext);
}
function useTableFocusProps(options = {}) {
  const { focus, setFocus } = useInvestigationFocus();
  const userClick = options.onRowClick;
  return {
    activeRowId: focus?.kind === "transaction" ? focus.id : void 0,
    onRowClick: React2.useCallback(
      (row) => {
        const target = focusFromTransactionRow(row);
        if (target) setFocus(target);
        userClick?.(row);
      },
      [setFocus, userClick]
    )
  };
}
function useGraphFocusProps(options = {}) {
  const { focus, setFocus } = useInvestigationFocus();
  const { onNodeClick: userClick, toNodeId, parseNodeId } = options;
  return {
    focusNodeId: focus ? toNodeId ? toNodeId(focus) : focusNodeId(focus) : void 0,
    onNodeClick: React2.useCallback(
      (node) => {
        const target = parseNodeId?.(node.id) ?? parseFocusNodeId(node.id) ?? { kind: "transaction", id: node.id };
        setFocus(target);
        userClick?.(node);
      },
      [parseNodeId, setFocus, userClick]
    )
  };
}
function PanelLoading({ label }) {
  return /* @__PURE__ */ jsxs("div", { className: "flex h-full min-h-0 flex-col gap-3 p-4", "aria-busy": true, children: [
    /* @__PURE__ */ jsx(Skeleton, { className: "h-6 w-40" }),
    /* @__PURE__ */ jsx(Skeleton, { className: "h-4 w-full" }),
    /* @__PURE__ */ jsx(Skeleton, { className: "h-4 w-5/6" }),
    /* @__PURE__ */ jsx(Skeleton, { className: "flex-1" }),
    /* @__PURE__ */ jsxs("span", { className: "sr-only", children: [
      "Loading ",
      label,
      "\u2026"
    ] })
  ] });
}
function MissingPeer({ label }) {
  return /* @__PURE__ */ jsx(
    EmptyState,
    {
      size: "lg",
      title: `${label} unavailable`,
      description: `This panel needs an optional package that isn't installed. Add the matching @authe view package to enable the ${label.toLowerCase()}.`
    }
  );
}
function LazyPanel({ label, className, children }) {
  return /* @__PURE__ */ jsx("div", { className: cn("h-full min-h-0", className), children: /* @__PURE__ */ jsx(ErrorBoundary, { boundary: `workspace:${label}`, fallback: () => /* @__PURE__ */ jsx(MissingPeer, { label }), children: /* @__PURE__ */ jsx(React2.Suspense, { fallback: /* @__PURE__ */ jsx(PanelLoading, { label }), children }) }) });
}
var LazyTransactionTable = React2.lazy(
  () => import('@authe/tx-table').then((m) => ({ default: m.TransactionTable }))
);
function TablePanel(props) {
  return /* @__PURE__ */ jsx(LazyPanel, { label: "Table", children: /* @__PURE__ */ jsx("div", { className: "h-full overflow-auto p-3", children: /* @__PURE__ */ jsx(LazyTransactionTable, { ...props }) }) });
}
var LazyGraphCanvas = React2.lazy(
  () => import('@authe/tx-graph').then((m) => ({ default: m.GraphCanvas }))
);
function GraphPanel(props) {
  return (
    // A frame, like `ScenePanel`: `GraphCanvas` is `h-full w-full`, which needs a
    // box whose height a percentage can resolve against — the frame guarantees
    // one however the host sized the panel.
    /* @__PURE__ */ jsx(GraphFrame, { className: "h-full w-full", children: /* @__PURE__ */ jsx(LazyPanel, { label: "Graph", children: /* @__PURE__ */ jsx(LazyGraphCanvas, { ...props }) }) })
  );
}
var LazySceneGraphView = React2.lazy(
  () => import('@authe/tx-graph').then((m) => ({ default: m.SceneGraphView }))
);
function ScenePanel(props) {
  return (
    // The panel's box IS the graph's box: a frame, so the scene renderer fills
    // the tab it was mounted in instead of drawing its own fixed height inside
    // it — and so no host has to pass `height="100%"`. Outside `LazyPanel`, so
    // the box exists while the (heavy) graph chunk is still loading and the
    // skeleton fills it too.
    /* @__PURE__ */ jsx(GraphFrame, { className: "h-full w-full", children: /* @__PURE__ */ jsx(LazyPanel, { label: "Graph", children: /* @__PURE__ */ jsx(LazySceneGraphView, { ...props }) }) })
  );
}
var LazySemanticView = React2.lazy(
  () => import('@authe/tx-semantic').then((m) => ({ default: m.SemanticView }))
);
function SemanticPanel(props) {
  return /* @__PURE__ */ jsx(LazyPanel, { label: "Semantic", children: /* @__PURE__ */ jsx("div", { className: "h-full w-full overflow-auto p-4", children: /* @__PURE__ */ jsx(LazySemanticView, { ...props }) }) });
}
var LazyPipelineBuilderStudio = React2.lazy(
  () => import('@authe/pipeline-builder').then((m) => ({ default: m.PipelineBuilderStudio }))
);
function PipelinePanel(props) {
  return /* @__PURE__ */ jsx(LazyPanel, { label: "Pipeline", children: /* @__PURE__ */ jsx("div", { className: "h-full w-full", children: /* @__PURE__ */ jsx(LazyPipelineBuilderStudio, { ...props }) }) });
}

export { DEFAULT_NOTES_KEY, GRAPH_ANNOTATIONS_KEY, GraphPanel, InvestigationFocusProvider, InvestigationWorkspace, LazyPanel, NotesPanel, PipelinePanel, ScenePanel, SemanticPanel, TablePanel, WORKSPACE_PACKAGE, WorkspaceComments, WorkspaceProvider, WorkspaceViewId, graphAuthoringFrom, panelCommentAnchor, standardInvestigationPanels, useGraphAnnotations, useGraphFocusProps, useInvestigationFocus, useOptionalInvestigationFocus, useTableFocusProps, viewAnchor };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map